import React, { useRef } from 'react';
import { useNavigate } from 'react-router';
import { motion } from 'motion/react';
import { Button } from '../components/mtt/Button';
import { NarrativeCarousel } from '../components/mtt/NarrativeCarousel';
import { AssetManifest } from '../../assets/asset-manifest';
import { staggerContainer, staggerItem } from '../../lib/motion';
import { ElegantGradientBackground } from '../components/ui/anim/ElegantGradientBackground';
import { TextReveal } from '../components/ui/anim/TextReveal';
import { FloatingPaths } from '@/components/kokonutui/background-paths';
import { gsap, useGSAP, prefersReducedMotion } from '../../lib/animation';

const slides = [
  {
    id: '1',
    image: AssetManifest.features.welcomeCommunity,
    title: 'Everyone has a tale.',
    subtitle: 'Start telling yours — preserve your wisdom and memories for those who matter most.',
  },
  {
    id: '2',
    image: AssetManifest.characters.grandparentsGrandchildren,
    title: 'Your legacy, beautifully told.',
    subtitle: 'Share the moments that shaped your life and values with the people you love.',
  },
  {
    id: '3',
    image: AssetManifest.features.afterStateFamily,
    title: 'Connect generations.',
    subtitle: 'Every family has stories worth keeping. Start yours today.',
  },
];

export function WelcomeScreen() {
  const navigate = useNavigate();
  const desktopCardRef = useRef<HTMLDivElement>(null);

  // Desktop card follows the pointer with a gentle 3D tilt. quickTo reuses one tween per
  // axis, so pointermove never allocates new animations (smooth even at 120Hz).
  useGSAP(() => {
    const card = desktopCardRef.current;
    if (!card || prefersReducedMotion() || !window.matchMedia('(pointer: fine)').matches) return;

    gsap.set(card, { transformPerspective: 1600 });
    const rotateX = gsap.quickTo(card, 'rotationX', { duration: 0.8, ease: 'power3.out' });
    const rotateY = gsap.quickTo(card, 'rotationY', { duration: 0.8, ease: 'power3.out' });

    const onMove = (e: PointerEvent) => {
      const x = e.clientX / window.innerWidth - 0.5;
      const y = e.clientY / window.innerHeight - 0.5;
      rotateY(x * 3);
      rotateX(-y * 3);
    };
    const onLeave = () => {
      rotateX(0);
      rotateY(0);
    };
    window.addEventListener('pointermove', onMove);
    document.addEventListener('pointerleave', onLeave);
    return () => {
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerleave', onLeave);
    };
  });

  return (
    <ElegantGradientBackground containerClassName="min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8">
      {/* Kokonut UI flowing paths, in brand colours, drifting behind the welcome card. */}
      <div className="fixed inset-0 opacity-70">
        <FloatingPaths position={1} />
      </div>

      {/* ── Mobile + Tablet (<1024px) ── */}
      <div
        className="lg:hidden w-full max-w-md bg-[var(--surface)] border-[var(--border-width-hairline)] border-solid rounded-[var(--radius-xl)] shadow-[var(--elevation-md)] overflow-hidden relative z-10"
        style={{ borderColor: 'var(--border)' }}
      >
        <NarrativeCarousel slides={slides} />
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="p-6 sm:p-8 text-center"
        >
          <motion.div variants={staggerItem} className="mb-[var(--gap-sm)] flex flex-wrap justify-center font-bold text-2xl">
            <TextReveal text="Everyone has a tale." className="justify-center" />
            <TextReveal text="Start telling yours." className="justify-center" wordClassName="text-[var(--primitive-ember-500)]" delay={0.3} />
          </motion.div>
          <motion.p variants={staggerItem} style={{ color: 'var(--text-tertiary)' }} className="mb-[var(--gap-lg)]">
            Preserve your stories for the people you love most.
          </motion.p>
          <motion.div variants={staggerItem} className="space-y-[var(--gap-sm)]">
            <Button variant="primary" fullWidth onClick={() => navigate('/signup')}>Get Started</Button>
            <Button variant="utility" fullWidth onClick={() => navigate('/signin')}>Sign In</Button>
          </motion.div>
          <motion.p
            variants={staggerItem}
            className="mt-[var(--gap-md)] text-center"
            style={{ fontSize: 'var(--text-size-xs)', color: 'var(--text-tertiary)' }}
          >
            Trusted by families in 40+ countries
          </motion.p>
        </motion.div>
      </div>

      {/* ── Desktop (1024px+): two-column split, centered in page ── */}
      <div
        ref={desktopCardRef}
        className="hidden lg:flex w-full max-w-7xl bg-[var(--surface)] relative z-10 will-change-transform"
        style={{
          height: '85vh',
          maxHeight: '900px',
          border: '1.5px solid var(--border)',
          boxShadow: 'var(--elevation-xl)',
          borderRadius: 'var(--radius-xl)',
          overflow: 'hidden'
        }}
      >

        {/* Left panel: carousel forced to fill full height */}
        <div className="flex-[1.5] relative h-full">
          <div
            className="welcome-carousel-fullheight"
            style={{ height: '100%', width: '100%' }}
          >
            <NarrativeCarousel slides={slides} borderRadius="left" />
          </div>
        </div>

        {/* Right panel: content centered */}
        <div className="flex-1 flex items-center justify-center p-8 xl:p-12">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="w-full max-w-[400px]"
          >
            {/* Logo */}
            <motion.img
              variants={staggerItem}
              src={AssetManifest.brand.logoPrimary}
              alt="My Telling Tale"
              className="h-20 w-20 object-contain mb-[var(--gap-lg)]"
            />

            {/* Headline */}
            <motion.div variants={staggerItem} className="mb-[var(--gap-md)] text-4xl font-bold leading-tight">
              <TextReveal text="Everyone has a tale." />
              <TextReveal text="Start telling yours." wordClassName="text-[var(--primitive-ember-500)]" delay={0.3} />
            </motion.div>

            <motion.p variants={staggerItem} style={{ color: 'var(--text-tertiary)', lineHeight: 1.7 }} className="mb-[var(--gap-lg)]">
              Preserve your stories, wisdom, and memories with the people you love.
              Create a lasting legacy that connects generations.
            </motion.p>

            {/* CTAs */}
            <motion.div variants={staggerItem} className="space-y-[var(--gap-sm)]">
              <Button variant="primary" fullWidth size="large" onClick={() => navigate('/signup')}>
                Get Started
              </Button>
              <Button variant="utility" fullWidth size="large" onClick={() => navigate('/signin')}>
                Sign In
              </Button>
            </motion.div>

            <motion.p
              variants={staggerItem}
              className="mt-[var(--gap-lg)] text-center"
              style={{ fontSize: 'var(--text-size-xs)', color: 'var(--text-tertiary)' }}
            >
              Trusted by families in 40+ countries
            </motion.p>
          </motion.div>
        </div>
      </div>

    </ElegantGradientBackground>
  );
}